import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import { formatSerpText } from '@/utils/approval/serpDocument';
import {
  findSerpBulkSummaryRow,
  formatSerpBulkAmount,
  getSerpBulkInspectionRows,
  getSerpBulkNumber,
  getSerpBulkSettlementRows,
  getSerpBulkShops,
  SERP_BULK_SUMMARY_SEASON,
  sumSerpBulkSettlement,
  type SerpBulkShop,
  type SerpBulkStockAdjustmentSource,
} from '@/utils/approval/serpBulkStockAdjustment';

const TITLE_BG = '#fff2cc';
const LABEL_BG = '#f3f3f3';
const BOLD_ROW = { fontWeight: 'bold' } as const;

const bg = (color: string) => ({ backgroundColor: color });

/** 저장 HTML 의 매장별 상세 숨김 영역 id — 레거시 문서와 같은 이름 */
export const DETAIL_ID_PREFIX = 'shop_modal_data_';
export const SHOP_CODE_ATTR = 'data-shop-cd';

const orDash = (value: unknown) => formatSerpText(value) || '-';

type ShopDetailProps = {
  source: SerpBulkStockAdjustmentSource;
  shop: SerpBulkShop;
  t: TFunction;
};

/** 레거시 generateShopModalHTML 의 표 3개(칸 구성·색 동일) */
const SerpBulkShopDetail = ({ source, shop, t }: ShopDetailProps) => {
  const inspections = getSerpBulkInspectionRows(shop);
  const settlements = getSerpBulkSettlementRows(shop);
  const totals = sumSerpBulkSettlement(settlements);
  const excAmount = getSerpBulkNumber(shop.EXC_AMT_NUM).toLocaleString('ko-KR');
  const suretyAmount = getSerpBulkNumber(
    shop.SM_SURETY_BOND_AMT_NUM,
  ).toLocaleString('ko-KR');

  return (
    <>
      <table className="table table-bordered mb-30 text-center">
        <tbody>
          <tr>
            <th style={bg(TITLE_BG)} colSpan={3}>
              {orDash(source.BRAND_NM)}
            </th>
            <th style={bg(TITLE_BG)} colSpan={3}>
              {orDash(shop.SHOP_NM)}
            </th>
          </tr>
          <tr>
            <th style={bg(LABEL_BG)} rowSpan={2}>
              {t('정산 대상자')}
            </th>
            <th style={bg(LABEL_BG)}>{t('코드')}</th>
            <th style={bg(LABEL_BG)}>{t('이름')}</th>
            <th style={bg(LABEL_BG)}>{t('근무시작일')}</th>
            <th style={bg(LABEL_BG)}>{t('사업자번호')}</th>
            <th style={bg(LABEL_BG)}>{t('비고')}</th>
          </tr>
          <tr>
            <td>{orDash(shop.SM_SHOP_CD)}</td>
            <td>{orDash(shop.SM_SHOP_NM)}</td>
            <td>{orDash(shop.SM_SHOP_STS_DE)}</td>
            <td>{orDash(shop.SM_SHOP_BRN)}</td>
            <td>{orDash(formatSerpText(shop.SM_SHOP_RMK).trim())}</td>
          </tr>
          <tr>
            <th style={bg(LABEL_BG)}>{t('재고조사 유형')}</th>
            <td colSpan={5}>{orDash(source.STK_INVES_TYP_NM)}</td>
          </tr>
          <tr>
            <th style={bg(LABEL_BG)}>{t('재고조사 시행 일자')}</th>
            <td colSpan={5}>{orDash(shop.STCKT_DE)}</td>
          </tr>
        </tbody>
      </table>
      <table className="table table-bordered mb-30 text-center">
        <thead>
          <tr>
            <th style={bg(TITLE_BG)} colSpan={8}>
              {t('매장실사내용')}
            </th>
          </tr>
          <tr>
            <th style={bg(LABEL_BG)} rowSpan={2}>
              {t('시즌')}
            </th>
            <th style={bg(LABEL_BG)} colSpan={2}>
              {t('전산')}
            </th>
            <th style={bg(LABEL_BG)} colSpan={2}>
              {t('실사')}
            </th>
            <th style={bg(LABEL_BG)} colSpan={2}>
              {t('차이')}
            </th>
            <th style={bg(LABEL_BG)} rowSpan={2}>
              {t('Loss 금액')}
            </th>
          </tr>
          <tr>
            <th style={bg(LABEL_BG)}>{t('수량')}</th>
            <th style={bg(LABEL_BG)}>{t('택가')}</th>
            <th style={bg(LABEL_BG)}>{t('수량')}</th>
            <th style={bg(LABEL_BG)}>{t('택가')}</th>
            <th style={bg(LABEL_BG)}>{t('수량')}</th>
            <th style={bg(LABEL_BG)}>{t('택가')}</th>
          </tr>
        </thead>
        <tbody>
          {inspections.map((row, index) => (
            <tr
              key={`inspection-${index}`}
              style={
                row.SSN_CD === SERP_BULK_SUMMARY_SEASON ? BOLD_ROW : undefined
              }
            >
              <td style={bg(LABEL_BG)}>{formatSerpText(row.SSN_CD)}</td>
              <td className="text-right">{formatSerpText(row.STD_STK_QTY)}</td>
              <td className="text-right">
                {formatSerpBulkAmount(row.SALE_AMT)}
              </td>
              <td className="text-right">{formatSerpText(row.STCKT_QTY)}</td>
              <td className="text-right">
                {formatSerpBulkAmount(row.STCKT_AMT)}
              </td>
              <td className="text-right">{formatSerpText(row.GAP_QTY)}</td>
              <td className="text-right">
                {formatSerpBulkAmount(row.GAP_AMT)}
              </td>
              <td className="text-right">
                {formatSerpBulkAmount(row.LOSS_AMT)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <table className="table table-bordered mb-30 text-center">
        <thead>
          <tr>
            <th style={bg(TITLE_BG)} colSpan={7}>
              {t('정산내역')}
            </th>
          </tr>
          <tr>
            <th style={bg(LABEL_BG)} colSpan={2}>
              {t('구분')}
            </th>
            <th style={bg(LABEL_BG)}>{t('Loss 금액')}</th>
            <th style={bg(LABEL_BG)}>{t('공급가율(%)')}</th>
            <th style={bg(LABEL_BG)}>{t('공제비율(%)')}</th>
            <th style={bg(LABEL_BG)}>{t('정산 금액')}</th>
            <th style={bg(LABEL_BG)}>{t('비고')}</th>
          </tr>
        </thead>
        <tbody>
          {settlements.map((row, index) => (
            <tr key={`settlement-${index}`}>
              {index === 0 && (
                <th style={bg(LABEL_BG)} rowSpan={settlements.length}>
                  Loss
                </th>
              )}
              <td style={bg(LABEL_BG)}>{formatSerpText(row.SSN_CD)}</td>
              <td className="text-right">
                {formatSerpBulkAmount(row.LOSS_AMT)}
              </td>
              <td>{formatSerpText(row.GI_RAT)}</td>
              <td>{formatSerpText(row.DED_RTO)}</td>
              <td className="text-right">
                {row.STMT_AMT ? formatSerpBulkAmount(row.STMT_AMT) : '0'}
              </td>
              <td>{formatSerpText(row.APRV_RMK)}</td>
            </tr>
          ))}
          {settlements.length > 0 && (
            <>
              <tr>
                <th style={bg(LABEL_BG)} colSpan={2}>
                  {t('미처리금액')}
                </th>
                <td className="text-right">{excAmount}</td>
                <td />
                <td />
                <td className="text-right">{excAmount}</td>
                <td />
              </tr>
              <tr style={BOLD_ROW}>
                <th style={bg(LABEL_BG)} colSpan={2}>
                  {t('합계')}
                </th>
                <td className="text-right">
                  {totals.loss.toLocaleString('ko-KR')}
                </td>
                <td />
                <td />
                <td className="text-right">
                  {totals.settlement.toLocaleString('ko-KR')}
                </td>
                <td />
              </tr>
              <tr>
                <th style={bg(LABEL_BG)} colSpan={2}>
                  {t('보증보험금')}
                </th>
                <td className="text-right">{suretyAmount}</td>
                <td />
                <td />
                <td className="text-right">{suretyAmount}</td>
                <td />
              </tr>
            </>
          )}
        </tbody>
      </table>
    </>
  );
};

type ContentProps = {
  source: SerpBulkStockAdjustmentSource;
  t: TFunction;
};

/** 레거시 #APV_SERP_INVENTORY_COUNT .form-group — 머리표·매장별 내역 + 매장별 상세 숨김 영역 */
const SerpBulkStockAdjustmentContent = ({ source, t }: ContentProps) => {
  const shops = getSerpBulkShops(source);
  return (
    <>
      <div className="apv-serp-inventory-count">
        <div className="mt-5 pb-10">
          <table className="table table-bordered mb-30 text-center">
            <tbody>
              <tr>
                <th style={bg(LABEL_BG)}>{t('브랜드')}</th>
                <th style={bg(LABEL_BG)}>{t('재고조사 유형')}</th>
                <th style={bg(LABEL_BG)}>{t('요청차수')}</th>
              </tr>
              <tr>
                <td>{formatSerpText(source.BRAND_NM)}</td>
                <td>{formatSerpText(source.STK_INVES_TYP_NM)}</td>
                <td>{formatSerpText(source.INSTR_DEG)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="d-flex justify-content-between">
          <strong className="font-md">{t('매장별 내역')}</strong>
          <span>{t('단위: 원')}</span>
        </div>
        <div className="mt-5 pb-10">
          <table className="table table-bordered mb-30 text-center">
            <tbody>
              <tr>
                <th style={bg(LABEL_BG)}>{t('실사일')}</th>
                <th style={bg(LABEL_BG)}>{t('매장코드')}</th>
                <th style={bg(LABEL_BG)}>{t('매장명')}</th>
                <th style={bg(LABEL_BG)}>{t('차이 수량')}</th>
                <th style={bg(LABEL_BG)}>{t('차이 금액')}</th>
                <th style={bg(LABEL_BG)}>{t('차이 로스가')}</th>
                <th style={bg(LABEL_BG)}>{t('상세')}</th>
              </tr>
              {shops.map((shop, index) => {
                const summary = findSerpBulkSummaryRow(shop);
                return (
                  <tr key={`shop-${index}`}>
                    <td>{formatSerpText(shop.STCKT_DE)}</td>
                    <td>{formatSerpText(shop.SHOP_CD)}</td>
                    <td>{formatSerpText(shop.SHOP_NM)}</td>
                    <td className="text-right">
                      {summary ? formatSerpText(summary.GAP_QTY) : ''}
                    </td>
                    <td className="text-right">
                      {summary ? formatSerpBulkAmount(summary.GAP_AMT) : ''}
                    </td>
                    <td className="text-right">
                      {summary ? formatSerpBulkAmount(summary.LOSS_AMT) : ''}
                    </td>
                    <td className="td-show-detail">
                      <button
                        type="button"
                        className="btn btn-white"
                        {...{ [SHOP_CODE_ATTR]: formatSerpText(shop.SHOP_CD) }}
                      >
                        {t('내역 보기')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {shops.map((shop, index) => (
        <div
          key={`detail-${index}`}
          id={`${DETAIL_ID_PREFIX}${formatSerpText(shop.SHOP_CD)}`}
          style={{ display: 'none' }}
        >
          <SerpBulkShopDetail source={source} shop={shop} t={t} />
        </div>
      ))}
    </>
  );
};

/** attributes.content — 조회 화면은 이 HTML 만으로 표·매장별 상세를 그림 */
export const buildSerpBulkStockAdjustmentContentHtml = (
  source: SerpBulkStockAdjustmentSource,
  t: TFunction,
): string =>
  renderToStaticMarkup(
    <div className="form-group">
      <SerpBulkStockAdjustmentContent source={source} t={t} />
    </div>,
  );
